/**
 * DataFacade — typed read surface over a numeric-series
 * dataSource driven by a `DATA_PRESETS` entry.
 *
 * Obtained via `row.as(DATA_FACADE)`. The facade resolves the
 * preset from `row.lookupKey` and rebuilds the underlying
 * `DataIndex` on `ColumnDataChanged(rowId)` or `RowDataChanged
 * (rowId, "lookupKey")`. Queries are read-only — presets describe
 * derived/provider series, not user-edited ones.
 *
 * Returns queries on the empty index when the row has no
 * matching preset; `.diagnostics()` surfaces whether the preset
 * resolved (via `graphProps != null`).
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate, TimezoneRegion } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

import { FacadeKey, type FacadeFactory } from "./facade.ts";
import { deepFrozen, frozenMap } from "../core/frozen-collections.ts";
import {
  ColumnDataChanged,
  type Invalidation,
  RowDataChanged,
} from "../tx/invalidation.ts";
import { DataIndex } from "../indices/data-index.ts";
import { sheetOf } from "../sheet/sheet-stores.ts";
import type { Graph, PointY } from "../stores/graph-types.ts";
import { dataCanvasGraphNodes } from "../indices/data/row-graph-nodes.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";
import { Internals } from "../core/internals.ts";

/** The facade key that reaches a source row's `DataFacade`. */
export const DATA_FACADE = new FacadeKey<DataFacade>("data");

/** `DataFacade`'s friend record (`Internals`): its constructor registers each instance. */
type DataFacadeInternals = {
  readonly dispose: (facade: DataFacade) => void;
  readonly rebuild: (facade: DataFacade) => void;
};
const dataFacadeInternals = new Internals<DataFacade, DataFacadeInternals>("DataFacade");

/**
 * Tear down `facade`'s store subscription. The SDK-private door onto the
 * private `dispose` — a facade is cached per row and shared by every reader
 * of it (`row.as(key)`), so a consumer must not be able to stop it under the
 * others. Not on a barrel.
 */
export function disposeDataFacade(facade: DataFacade): void {
  dataFacadeInternals.of(facade).dispose(facade);
}

/**
 * Rebuild `facade`'s index from its row now — for a front-end that loaded
 * the row's cells outside the invalidation pipeline (the data alias's store
 * after its range fetch). The SDK-private door onto the private rebuild: the
 * facade a read hands out (`row.as(DATA_FACADE)`) carries reads only. On
 * `@tento-lona/sheets/internal-ui` and `…/internal-testing`, not on a barrel.
 */
export function rebuildDataFacade(facade: DataFacade): void {
  dataFacadeInternals.of(facade).rebuild(facade);
}

/** A source row's numeric data as series, for charts and summaries. */
export class DataFacade {
  static readonly #internals: DataFacadeInternals = {
    dispose: (facade) => facade.#dispose(),
    rebuild: (facade) => facade.#rebuild(),
  };

  readonly #index = new DataIndex();
  #dirty = true;
  /** What `peekAllSeries` hands out: a frozen copy of the index's series, rebuilt with the index. */
  #seriesView: ReadonlyMap<string, DataIndex.Series> = frozenMap([]);
  readonly #unsubscribe: () => void;
  /**
   * Listeners notified whenever this facade's underlying data is invalidated
   * (a `ColumnDataChanged` for its row, or a lookupKey change) — i.e. the next
   * query will rebuild with different cells/graphProps. A UI layer registers
   * one to re-bind a view when async cell data arrives AFTER the initial
   * (empty) bind: `peekDataFrame`/`peekSeries` return the empty index on first
   * read, and nothing else re-invokes the binder when the fetch lands.
   */
  readonly #dataChangeListeners = new Set<() => void>();

  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    dataFacadeInternals.register(this, DataFacade.#internals);
    this.#row = row;
    this.#unsubscribe = row.sheetReads.invalidationSink.subscribe(
      (e: Invalidation) => {
        const relevant =
          (e instanceof ColumnDataChanged && e.rowId === row.id) ||
          (e instanceof RowDataChanged &&
            e.rowId === row.id &&
            e.field === "lookupKey");
        if (!relevant) return;
        this.#dirty = true;
        for (const listener of this.#dataChangeListeners) listener();
      },
    );
  }

  /**
   * Register a listener invoked when this facade's data is invalidated (see
   * `dataChangeListeners`). Returns an unsubscribe. The listener runs inside
   * the invalidation drain, so a consumer that re-binds DOM SHOULD defer
   * (e.g. `queueMicrotask`) to avoid re-entrancy.
   */
  onDataChanged(listener: () => void): Unsubscribe {
    this.#dataChangeListeners.add(listener);
    return () => this.#dataChangeListeners.delete(listener);
  }

  /** Stop listening to the row's store. Reached through `disposeDataFacade`. */
  #dispose(): void {
    this.#unsubscribe();
    this.#dataChangeListeners.clear();
  }

  // ── Queries (trigger lazy rebuild) ────────────────────────────────

  /** Graph.Props derived from the preset, or null if no preset matched. */
  graphProps(): Option<Graph.Props> {
    this.#refreshIfDirty();
    return this.#index.graphProps;
  }

  peekSeries(key: string): Option<DataIndex.Series> {
    this.#refreshIfDirty();
    return this.#index.peekSeries(key);
  }

  /** See `DataIndex.peekPointsInRange`. */
  peekPointsInRange<Y extends PointY>(
    key: DataIndex.SeriesKey<Y>,
    start: number,
    end: number,
  ): readonly [number, Y][] {
    this.#refreshIfDirty();
    return this.#index.peekPointsInRange(key, start, end);
  }

  /** Every series, by key: a frozen map (it refuses writes), one per rebuild. */
  peekAllSeries(): ReadonlyMap<string, DataIndex.Series> {
    this.#refreshIfDirty();
    return this.#seriesView;
  }

  /** Column-scoped DataFrame — null if no preset matched. */
  peekDataFrame(
    range: NaiveDate.Range,
    tz: TimezoneRegion,
  ): Option<DataIndex.DataFrame> {
    this.#refreshIfDirty();
    return this.#index.peekDataFrame(range, tz);
  }

  diagnostics(): ReturnType<DataIndex["diagnostics"]> {
    this.#refreshIfDirty();
    return Object.freeze(this.#index.diagnostics());
  }

  /**
   * Explicit rebuild — when a caller knows the preset or cells changed
   * outside the invalidation pipeline. Reached through `rebuildDataFacade`.
   */
  #rebuild(): void {
    this.#dirty = true;
    this.#refreshIfDirty();
  }

  // ── Rebuild ────────────────────────────────────────────────────────

  #refreshIfDirty(): void {
    if (!this.#dirty) return;
    const lookupKey = this.#row.sheetReads.rowMeta(this.#row.id)
      .lookupKey ?? null;
    // The preset registered for `lookupKey` is the built-in default; when none
    // matches, the sheet's OWN rows may carry the chart config (a `data-canvas`
    // parent whose children are the `canvas.*` renderer + `data-series` rows).
    // Rows fill the no-preset case; a matching preset still wins (conservative
    // — see `row-graph-nodes.ts`). Both reduce to a `GraphSpecNode[]`.
    const presetSpecs = lookupKey ? sheetOf(this.#row).registries.dataPresets.get(lookupKey) : null;
    const rowsNodes = presetSpecs != null ? null : dataCanvasGraphNodes(this.#row);
    const nodes = presetSpecs ?? rowsNodes;
    const all = this.#row.peekCells(null);
    const cells = SourceCells.dates(all);
    // One-shot per hydrate/invalidation — which config source won (built-in
    // preset vs the sheet's own rows vs neither), so a live session can see
    // whether the chart config was even resolved before extraction runs.
    Tracing.log("rows-chart/facade-refresh", {
      rowId: this.#row.id.toString(),
      lookupKey,
      source: presetSpecs != null ? "preset" : rowsNodes != null ? "rows" : "none",
      nodeCount: nodes?.length ?? 0,
      cellCount: cells.length,
    });
    this.#index.populateFromNodes(nodes, cells, SourceCells.customs(all));
    this.#seriesView = frozenMap(this.#index.peekAllSeries());
    this.#dirty = false;
  }
}

/** Values that go with `DataFacade`: `factory`. */
export namespace DataFacade {
  /**
   * Factory registered on the sheet's `FacadeRegistry`. Attaches
   * to every source row; query-time probe determines whether the
   * preset actually resolves.
   */
  export const factory: FacadeFactory<DataFacade> = deepFrozen({
    key: DATA_FACADE,
    build(row: SourceRow): Option<DataFacade> {
      const attached = row.descriptor.role === "source";
      // One-shot per row: whether the data facade attached and why. Attachment
      // is role-gated ONLY (any source row qualifies) — NOT preset-gated — so a
      // rows-driven source with a no-preset lookupKey is still eligible.
      Tracing.log("rows-chart/facade-attach", {
        rowId: row.id.toString(),
        role: row.descriptor.role,
        lookupKey: row.lookupKey ?? null,
        attached,
      });
      if (!attached) return null;
      return new DataFacade(row);
    },
  });
}
